import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Select from "react-select";
import { IoCloseOutline } from "react-icons/io5";
import API_URL from "../../src/Config";
import "../forms/voyageForm.css";
import {
  EXCAVATION_PERMIT_LABEL,
  buildDepartmentOptions,
  buildReviewerPayload,
  buildUserOptions,
  emptyReviewerRow,
  findExcavationPermitType,
  getReviewerUserId,
  validateReviewerRows,
} from "./excavationApprovalSetupUtils";

function AddExcavationReviewerConfiguration({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [permitOption, setPermitOption] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [rows, setRows] = useState([emptyReviewerRow()]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchFormOptions = useCallback(async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const [permitResponse, userResponse] = await Promise.all([
        axios.get(`${API_URL}/api/get_work_permit_types`, {
          headers: { Authorization: authKey },
        }),
        axios.get(`${API_URL}/api/get_user`, {
          headers: { Authorization: authKey },
        }),
      ]);

      const excavationPermit = findExcavationPermitType(
        permitResponse.data?.work_permit_types || []
      );

      if (!excavationPermit) {
        setSeverity("error");
        showSnackbar("Excavation permit type was not found.");
        return;
      }

      setPermitOption({
        value: excavationPermit.id,
        label: EXCAVATION_PERMIT_LABEL,
      });

      const setupResponse = await axios.get(
        `${API_URL}/api/permit/departments/setup/${excavationPermit.id}`,
        { headers: { Authorization: authKey } }
      );

      setDepartmentOptions(
        buildDepartmentOptions(setupResponse.data?.departments || [])
      );
      setUserOptions(buildUserOptions(userResponse.data || []));
    } catch (error) {
      console.error(
        "Error fetching excavation reviewer options:",
        error.message
      );
      setSeverity("error");
      showSnackbar("Unable to fetch reviewer configuration options.");
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchFormOptions();
  }, [fetchFormOptions]);

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setErrors((prevErrors) => ({ ...prevErrors, department_id: "" }));
  };

  const handleReviewerChange = (index, selectedOption) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              user_id: selectedOption ? getReviewerUserId(selectedOption) : "",
              user_name: selectedOption ? selectedOption.user_name : "",
              user_email: selectedOption ? selectedOption.user_email : "",
            }
          : row
      )
    );
    setErrors((prevErrors) => ({ ...prevErrors, [`user_id_${index}`]: "" }));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, emptyReviewerRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prevRows) =>
      prevRows.length > 1
        ? prevRows.filter((_, rowIndex) => rowIndex !== index)
        : prevRows
    );
  };

  const validateForm = () => {
    const validationErrors = validateReviewerRows(rows);
    if (!selectedDepartment?.value) {
      validationErrors.department_id = "Department is required.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      setSeverity("error");
      showSnackbar("Please fix the highlighted fields.");
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildReviewerPayload(selectedDepartment.value, rows);

      const response = await axios.post(
        `${API_URL}/api/permit/departments/reviewers`,
        payload,
        { headers: { Authorization: authKey } }
      );

      if (response.status === 201) {
        setSeverity("success");
        showSnackbar("Reviewer configuration added successfully");
        setTimeout(
          () => navigate("/excavation-reviewer-configuration"),
          1000
        );
      }
    } catch (error) {
      console.error("Error saving reviewer configuration:", error.message);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while saving reviewer configuration."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/excavation-reviewer-configuration");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New Excavation Reviewer Configuration</PageBackTitle>
              <div className="data-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail" onKeyDown={handleKeyDown}>
              <div className="new-data-form">
                <div className="form-group-select">
                  <label>
                    Permit Type<span className="text-danger">*</span>
                  </label>
                  <Select
                    options={permitOption ? [permitOption] : []}
                    value={permitOption}
                    isDisabled
                    placeholder="Excavation"
                  />
                </div>

                <div className="form-group-select">
                  <label>
                    Department<span className="text-danger">*</span>
                  </label>
                  <Select
                    options={departmentOptions}
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    placeholder="Select department"
                  />
                  {errors.department_id && (
                    <div className="error-message">{errors.department_id}</div>
                  )}
                </div>

                {rows.map((row, index) => (
                  <React.Fragment key={`reviewer-row-${index}`}>
                    <div className="form-group-select">
                      <label>
                        Reviewer {index + 1}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={userOptions}
                        value={
                          userOptions.find(
                            (option) => option.value === row.user_id
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleReviewerChange(index, selectedOption)
                        }
                        placeholder="Select reviewer"
                      />
                      {errors[`user_id_${index}`] && (
                        <div className="error-message">
                          {errors[`user_id_${index}`]}
                        </div>
                      )}
                    </div>

                    <div className="checkbox-form-group">
                      <label className="checkbox-form-group-label">
                        Permissions {index + 1}
                      </label>
                      <div className="fuel-checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={row.is_primary === "1"}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "is_primary",
                                event.target.checked ? "1" : "0"
                              )
                            }
                            className="voyage-checkbox"
                          />
                          Primary
                        </label>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={row.can_approve === "1"}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "can_approve",
                                event.target.checked ? "1" : "0"
                              )
                            }
                            className="voyage-checkbox"
                          />
                          Approve
                        </label>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={row.can_reject === "1"}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "can_reject",
                                event.target.checked ? "1" : "0"
                              )
                            }
                            className="voyage-checkbox"
                          />
                          Reject
                        </label>
                      </div>
                    </div>

                    {rows.length > 1 && (
                      <div className="form-group">
                        <label></label>
                        <div className="input-group">
                          <button
                            type="button"
                            className="data-form-cancel-button"
                            onClick={() => handleRemoveRow(index)}
                          >
                            <IoCloseOutline /> Remove Reviewer
                          </button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                <div className="form-group">
                  <label></label>
                  <div className="input-group">
                    <button
                      type="button"
                      className="data-form-save-button"
                      onClick={handleAddRow}
                    >
                      Add Reviewer
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default AddExcavationReviewerConfiguration;
